<template>
  <div class="container py-4">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h2 class="mb-1">Mitglieder</h2>
        <nav aria-label="breadcrumb">
          <ol class="breadcrumb">
            <li class="breadcrumb-item">
              <router-link :to="{ name: 'teams-list' }" class="text-decoration-none">
                Bereiche
              </router-link>
            </li>
            <li class="breadcrumb-item">
              <router-link
                :to="{ name: 'teams-detail', params: { id: $route.params.id } }"
                class="text-decoration-none"
              >
                Quittungsverwaltung
              </router-link>
            </li>
            <li class="breadcrumb-item">
              <router-link
                :to="{ name: 'teams-settings', params: { id: $route.params.id } }"
                class="text-decoration-none"
              >
                Bereich-Einstellungen
              </router-link>
            </li>
            <li class="breadcrumb-item active" aria-current="page">Mitglieder</li>
          </ol>
        </nav>
      </div>
      <div class="d-flex gap-2">
        <button
          class="btn btn-outline-primary"
          @click="exportSelectedMembers"
          :disabled="selectedMemberIds.length === 0 || exporting"
          v-if="isAdminOrCreator"
        >
          <font-awesome-icon icon="file-excel" class="me-2" />
          {{ exporting ? 'Exportiere...' : `Excel-Export (${selectedMemberIds.length})` }}
        </button>
        <button
          class="btn btn-outline-primary"
          @click="goToInvitationCodes"
          v-if="isAdminOrCreator"
        >
          <font-awesome-icon icon="key" class="me-1" />
          Einladungscodes
        </button>
        <button
          class="btn btn-outline-primary"
          @click="showExportSettingsModal"
          v-if="isAdminOrCreator"
        >
          <font-awesome-icon icon="cog" />
        </button>
        <button class="btn btn-outline-secondary" @click="goBack">
          <font-awesome-icon icon="arrow-left" /> Zurück
        </button>
      </div>
    </div>

    <MemberFieldsInfo class="mb-4">
      <router-link
        :to="{ name: 'teams-schema-edit', params: { id: $route.params.id } }"
        class="btn btn-outline-primary btn-sm"
      >
        <font-awesome-icon icon="pen" class="me-1" />
        Mitgliederfelder bearbeiten
      </router-link>
    </MemberFieldsInfo>

    <!-- Search and Filter -->
    <div class="card mb-4">
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-6">
            <div class="input-group">
              <span class="input-group-text">
                <font-awesome-icon icon="search" />
              </span>
              <input
                v-model="searchQuery"
                type="text"
                class="form-control"
                placeholder="Nach Name, E-Mail oder Mitgliederfeldern suchen..."
                @input="handleSearch"
              />
              <button
                v-if="searchQuery"
                class="btn btn-outline-secondary"
                type="button"
                @click="clearSearch"
              >
                <font-awesome-icon icon="times" />
              </button>
            </div>
          </div>
          <div class="col-md-3">
            <select v-model="roleFilter" class="form-select" @change="handleFilterChange">
              <option value="">Alle Rollen</option>
              <option value="teamAdmin">Bereich-Administratoren</option>
              <option value="teamUser">Bereich-Mitglieder</option>
              <option value="creator">Ersteller</option>
            </select>
          </div>
          <div class="col-md-3">
            <select v-model="pageSize" class="form-select" @change="handlePageSizeChange">
              <option value="10">10 pro Seite</option>
              <option value="25">25 pro Seite</option>
              <option value="50">50 pro Seite</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- Beitrittsanfragen (nur für Admins/Creator) -->
    <div v-if="isAdminOrCreator && joinRequests.length > 0" class="card mb-4">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="mb-0">
          <font-awesome-icon icon="user-clock" class="me-2" />
          Beitrittsanfragen
        </h5>
        <span class="badge bg-primary">{{ joinRequests.length }}</span>
      </div>
      <div class="card-body">
        <div v-if="loadingJoinRequests" class="text-center my-4">
          <div class="spinner-border" role="status">
            <span class="visually-hidden">Laden...</span>
          </div>
        </div>
        <div v-else-if="joinRequestsError" class="alert alert-danger" role="alert">
          <font-awesome-icon icon="exclamation-triangle" /> {{ joinRequestsError }}
        </div>
        <div v-else class="table-responsive">
          <table class="table table-hover">
            <thead>
              <tr>
                <th>Nutzer</th>
                <th>E-Mail</th>
                <th>Einladungscode</th>
                <th>Angefragt am</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="request in joinRequests" :key="request.id">
                <td>{{ request.user?.firstName }} {{ request.user?.lastName }}</td>
                <td>{{ request.user?.email }}</td>
                <td>
                  <code>{{ request.invitation?.code }}</code>
                </td>
                <td>{{ formatDate(request.requestedAt) }}</td>
                <td>
                  <div class="d-flex gap-1">
                    <button
                      class="btn btn-sm btn-info"
                      @click="showUserResponses(request)"
                      title="Antworten anzeigen"
                    >
                      <font-awesome-icon icon="eye" />
                    </button>
                    <button
                      class="btn btn-sm btn-success"
                      @click="approveJoinRequest(request.id)"
                      :disabled="processingRequest === request.id"
                      title="Genehmigen"
                    >
                      <span
                        v-if="processingRequest === request.id"
                        class="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      <font-awesome-icon v-else icon="check" />
                    </button>
                    <button
                      class="btn btn-sm btn-danger"
                      @click="showRejectRequestModal(request)"
                      title="Ablehnen"
                    >
                      <font-awesome-icon icon="times" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Members List -->
    <div class="card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="mb-0">
          <font-awesome-icon icon="users" class="me-2" />
          {{ totalMembers === 1 ? 'Mitglied' : 'Mitglieder' }}
          <span v-if="selectedMemberIds.length > 0" class="badge bg-primary ms-2">
            {{ selectedMemberIds.length }} </span
          ><span v-if="selectedMemberIds.length > 0" class="mx-2">/</span
          ><span class="badge bg-secondary">{{ totalMembers }}</span>
        </h5>
        <div class="d-flex align-items-center gap-3">
          <div v-if="filteredMembers.length > 0" class="text-muted small">
            {{ startIndex + 1 }}-{{ Math.min(startIndex + pageSize, filteredMembers.length) }} von
            {{ filteredMembers.length }}
            {{ searchQuery || roleFilter ? `(gefiltert von ${totalMembers} gesamt)` : '' }}
          </div>
          <!-- Bulk Selection Controls -->
          <div
            v-if="isAdminOrCreator && filteredMembers.length > 0"
            class="d-flex align-items-center gap-2 bulk-selection-controls"
          >
            <button
              class="btn btn-sm btn-outline-primary"
              @click="selectAllMembers"
              :disabled="allMembersSelected"
            >
              <font-awesome-icon icon="check-square" class="me-1" />
              Alle auswählen
            </button>
            <button
              class="btn btn-sm btn-outline-secondary"
              @click="deselectAllMembers"
              :disabled="selectedMemberIds.length === 0"
            >
              <font-awesome-icon icon="square" class="me-1" />
              Auswahl aufheben
            </button>

            <button class="btn btn-sm btn-outline-secondary" @click="showTableSettingsModal">
              <font-awesome-icon icon="table" />
            </button>
          </div>
        </div>
      </div>

      <div class="card-body">
        <div
          v-if="(loading && !serverSideFiltering) || (serverLoading && serverSideFiltering)"
          class="text-center my-4"
        >
          <div class="spinner-border" role="status">
            <span class="visually-hidden">Laden...</span>
          </div>
          <div class="mt-2 text-muted">
            {{
              serverSideFiltering
                ? 'Lade gefilterte Mitglieder vom Server...'
                : 'Lade alle Mitglieder...'
            }}
          </div>
        </div>
        <div v-else-if="error" class="alert alert-danger" role="alert">
          <font-awesome-icon icon="exclamation-triangle" /> {{ error }}
        </div>
        <div v-else-if="paginatedMembers.length === 0" class="text-center my-4">
          <font-awesome-icon icon="users-slash" size="3x" class="text-muted mb-3" />
          <p class="text-muted">
            {{
              searchQuery || roleFilter
                ? 'Keine Mitglieder gefunden, die den Filterkriterien entsprechen.'
                : 'Keine Mitglieder gefunden.'
            }}
          </p>
          <button
            v-if="searchQuery || roleFilter"
            class="btn btn-outline-primary"
            @click="clearAllFilters"
          >
            <font-awesome-icon icon="filter" class="me-1" />
            Filter zurücksetzen
          </button>
        </div>
        <div v-else class="table-responsive">
          <table class="table table-hover">
            <thead>
              <tr>
                <!-- Checkbox Column -->
                <th v-if="isAdminOrCreator" style="width: 40px" class="text-center">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    :checked="allMembersSelected"
                    @change="toggleAllMembers"
                  />
                </th>
                <th v-if="visibleTableColumns.includes('name')">
                  <button
                    class="btn p-0 text-decoration-none fw-bold border-0 bg-transparent"
                    @click="toggleSort('name')"
                  >
                    Name
                    <font-awesome-icon
                      v-if="sortField === 'name'"
                      :icon="sortDirection === 'asc' ? 'sort-up' : 'sort-down'"
                      class="ms-1"
                    />
                    <font-awesome-icon v-else icon="sort" class="ms-1 text-muted" />
                  </button>
                </th>
                <th v-if="visibleTableColumns.includes('email')">
                  <button
                    class="btn p-0 text-decoration-none fw-bold border-0 bg-transparent"
                    @click="toggleSort('email')"
                  >
                    E-Mail
                    <font-awesome-icon
                      v-if="sortField === 'email'"
                      :icon="sortDirection === 'asc' ? 'sort-up' : 'sort-down'"
                      class="ms-1"
                    />
                    <font-awesome-icon v-else icon="sort" class="ms-1 text-muted" />
                  </button>
                </th>
                <th v-if="visibleTableColumns.includes('role')">
                  <button
                    class="btn p-0 text-decoration-none fw-bold border-0 bg-transparent"
                    @click="toggleSort('role')"
                  >
                    Rolle
                    <font-awesome-icon
                      v-if="sortField === 'role'"
                      :icon="sortDirection === 'asc' ? 'sort-up' : 'sort-down'"
                      class="ms-1"
                    />
                    <font-awesome-icon v-else icon="sort" class="ms-1 text-muted" />
                  </button>
                </th>
                <th
                  v-if="
                    visibleTableColumns.includes('refundMethod') &&
                    isAdminOrCreator &&
                    selectedTeam?.refundMethod?.enabled
                  "
                >
                  <button
                    class="btn p-0 text-decoration-none fw-bold border-0 bg-transparent"
                    @click="toggleSort('refundMethod')"
                  >
                    Erstattung
                    <font-awesome-icon
                      v-if="sortField === 'refundMethod'"
                      :icon="sortDirection === 'asc' ? 'sort-up' : 'sort-down'"
                      class="ms-1"
                    />
                    <font-awesome-icon v-else icon="sort" class="ms-1 text-muted" />
                  </button>
                </th>

                <!-- Dynamische Bereich-Feld-Spalten -->
                <th
                  v-for="(field, index) in visibleTableFields"
                  :key="field?.fieldKey || `field-${index}`"
                >
                  <button
                    class="btn p-0 text-decoration-none fw-bold border-0 bg-transparent"
                    @click="toggleSort(field.fieldKey)"
                  >
                    {{ field?.name || 'Unbekanntes Feld' }}
                    <font-awesome-icon
                      v-if="sortField === field.fieldKey"
                      :icon="sortDirection === 'asc' ? 'sort-up' : 'sort-down'"
                      class="ms-1"
                    />
                    <font-awesome-icon v-else icon="sort" class="ms-1 text-muted" />
                  </button>
                </th>
                <th v-if="isAdminOrCreator" class="fw-bold">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="member in paginatedMembers"
                :key="member.id"
                class="member-row"
                :class="{ 'table-active': selectedMemberIds.includes(member?.user?.id) }"
                @click="toggleMemberSelection(member)"
                style="cursor: pointer"
              >
                <!-- Checkbox Column -->
                <td v-if="isAdminOrCreator" @click.stop class="text-center align-middle">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    :value="member?.user?.id"
                    v-model="selectedMemberIds"
                    @click.stop
                  />
                </td>
                <td v-if="visibleTableColumns.includes('name')">
                  <div class="d-flex align-items-center">
                    <div class="me-2">
                      <font-awesome-icon
                        :icon="getMemberIcon(member)"
                        :class="getMemberIconClass(member)"
                      />
                    </div>
                    <div>
                      <div class="fw-semibold">
                        {{ member?.user?.firstName || 'Unknown' }}
                        {{ member?.user?.lastName || 'Unknown' }}
                      </div>
                      <div v-if="member?.user?.id === currentUser?.id" class="small text-primary">
                        <font-awesome-icon icon="user" class="me-1" />
                        Du
                      </div>
                    </div>
                  </div>
                </td>
                <td v-if="visibleTableColumns.includes('email')">
                  {{ member?.user?.email || 'Keine E-Mail' }}
                </td>
                <td v-if="visibleTableColumns.includes('role')">
                  <span :class="getRoleBadgeClass(member)" class="badge bg-primary">
                    {{ mapRoles(member?.role) }}
                  </span>
                  <span
                    v-if="member?.user?.id === selectedTeam?.creatorId"
                    class="badge bg-danger ms-1"
                  >
                    Ersteller
                  </span>
                </td>
                <td
                  v-if="
                    visibleTableColumns.includes('refundMethod') &&
                    isAdminOrCreator &&
                    selectedTeam?.refundMethod?.enabled
                  "
                >
                  <div v-if="member.refundData">
                    <span
                      :class="getRefundMethodBadgeClass(member.refundData.refundMethod)"
                      class="badge"
                    >
                      {{ getRefundMethodLabel(member.refundData.refundMethod) }}
                    </span>
                    <div
                      v-if="member.refundData.refundMethod === 'paypal'"
                      class="small text-muted mt-1"
                    >
                      {{ member.refundData.paypalMeName }}
                    </div>
                    <div
                      v-else-if="member.refundData.refundMethod === 'bank_transfer'"
                      class="small text-muted mt-1"
                    >
                      {{ member.refundData.accountHolder }}
                    </div>
                  </div>
                  <span v-else class="text-muted small">
                    <font-awesome-icon icon="minus" />
                    Nicht konfiguriert
                  </span>
                </td>

                <!-- Dynamische Bereich-Feld-Spalten -->
                <td
                  v-for="(field, index) in visibleTableFields"
                  :key="field?.fieldKey || `field-${index}`"
                >
                  <div v-html="getFieldDisplayValue(member, field)"></div>
                </td>
                <td v-if="isAdminOrCreator" class="text-end align-middle" @click.stop>
                  <div class="d-flex flex-row gap-1">
                    <button
                      class="btn btn-sm btn-outline-primary"
                      @click="goToMemberReceipts(member)"
                      title="Quittungen anzeigen"
                    >
                      <font-awesome-icon icon="eye" />
                    </button>
                    <button
                      class="btn btn-sm btn-info"
                      @click="showMemberResponses(member)"
                      title="Antworten anzeigen"
                    >
                      <font-awesome-icon icon="comment-dots" />
                    </button>
                    <button
                      class="btn btn-sm btn-outline-success"
                      @click="exportMemberReceipts(member)"
                      :disabled="!hasReceipts(member)"
                      :title="
                        hasReceipts(member)
                          ? `Quittungen als ZIP-Archiv exportieren (${member.receiptCount} Quittungen)`
                          : 'Keine Quittungen vorhanden'
                      "
                    >
                      <font-awesome-icon icon="file-archive" />
                      <span v-if="hasReceipts(member)" class="ms-1 small"
                        >({{ member.receiptCount }})</span
                      >
                    </button>
                    <button
                      v-if="canRemoveMember(member)"
                      class="btn btn-sm btn-outline-danger"
                      @click="removeMember(member)"
                      title="Mitglied entfernen"
                    >
                      <font-awesome-icon icon="user-times" />
                    </button>
                    <button
                      v-if="canDemoteFromAdmin(member)"
                      class="btn btn-sm btn-outline-warning"
                      @click="demoteMemberFromAdmin(member)"
                      title="Admin-Rechte entfernen"
                    >
                      <font-awesome-icon icon="user-minus" />
                    </button>
                    <button
                      v-if="canPromoteToAdmin(member)"
                      class="btn btn-sm btn-outline-primary"
                      @click="promoteMemberToAdmin(member)"
                      title="Zu Admin befördern"
                    >
                      <font-awesome-icon icon="user-shield" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <nav v-if="totalPages > 1" aria-label="Mitglieder-Navigation" class="mt-4">
          <ul class="pagination justify-content-center">
            <li class="page-item" :class="{ disabled: currentPage === 1 }">
              <button class="page-link" @click="goToPage(1)" :disabled="currentPage === 1">
                <font-awesome-icon icon="angle-double-left" />
              </button>
            </li>
            <li class="page-item" :class="{ disabled: currentPage === 1 }">
              <button class="page-link" @click="previousPage" :disabled="currentPage === 1">
                <font-awesome-icon icon="angle-left" />
              </button>
            </li>

            <li
              v-for="page in visiblePages"
              :key="page"
              class="page-item"
              :class="{ active: page === currentPage }"
            >
              <button class="page-link" @click="goToPage(page)">
                {{ page }}
              </button>
            </li>

            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
              <button class="page-link" @click="nextPage" :disabled="currentPage === totalPages">
                <font-awesome-icon icon="angle-right" />
              </button>
            </li>
            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
              <button
                class="page-link"
                @click="goToPage(totalPages)"
                :disabled="currentPage === totalPages"
              >
                <font-awesome-icon icon="angle-double-right" />
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>

    <!-- Export Settings Modal -->
    <Teleport to="#modalTarget">
      <div
        v-if="showExportModal"
        class="modal show d-block"
        tabindex="-1"
        style="background-color: rgba(0, 0, 0, 0.5)"
      >
        <div class="modal-dialog modal-lg">
          <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <font-awesome-icon icon="cog" class="me-2" />
              Export-Einstellungen
            </h5>
            <button type="button" class="btn-close" @click="closeExportModal"></button>
          </div>
          <div class="modal-body">
            <div class="row">
              <div class="col-md-6">
                <h6 class="mb-3">Standard-Felder:</h6>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="export-col-name"
                    v-model="selectedColumns"
                    value="name"
                  />
                  <label class="form-check-label" for="export-col-name">Name</label>
                </div>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="export-col-email"
                    v-model="selectedColumns"
                    value="email"
                  />
                  <label class="form-check-label" for="export-col-email">E-Mail</label>
                </div>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="export-col-role"
                    v-model="selectedColumns"
                    value="role"
                  />
                  <label class="form-check-label" for="export-col-role">Rolle</label>
                </div>
                <div class="form-check" v-if="selectedTeam?.refundMethod?.enabled">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="export-col-refundMethod"
                    v-model="selectedColumns"
                    value="refundMethod"
                  />
                  <label class="form-check-label" for="export-col-refundMethod"
                    >Erstattungsmethode</label
                  >
                </div>
              </div>
              <div class="col-md-6">
                <h6 class="mb-3">Mitgliederfelder:</h6>
                <div v-for="field in exportFields" :key="field.fieldKey" class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :id="`export-col-${field.fieldKey}`"
                    v-model="selectedColumns"
                    :value="field.fieldKey"
                  />
                  <label class="form-check-label" :for="`export-col-${field.fieldKey}`">{{
                    field.name || 'Unbekanntes Feld'
                  }}</label>
                </div>
              </div>
            </div>
            <div class="mt-4">
              <div class="d-flex gap-2">
                <button class="btn btn-outline-primary" @click="selectAllExportColumns">
                  <font-awesome-icon icon="check-square" class="me-2" />
                  Alle auswählen
                </button>
                <button class="btn btn-outline-secondary" @click="resetExportColumns">
                  <font-awesome-icon icon="square" class="me-2" />
                  Standard-Felder
                </button>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeExportModal">
              Schließen
            </button>
            <button type="button" class="btn btn-primary" @click="closeExportModal">
              Übernehmen
            </button>
          </div>
        </div>
      </div>
    </div></Teleport>

    <!-- Table Settings Modal -->
    <Teleport to="#modalTarget">
      <div
        v-if="showTableModal"
        class="modal show d-block"
        tabindex="-1"
        style="background-color: rgba(0, 0, 0, 0.5)"
      >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <font-awesome-icon icon="table" class="me-2" />
              Tabellen-Einstellungen
            </h5>
            <button type="button" class="btn-close" @click="closeTableModal"></button>
          </div>
          <div class="modal-body">
            <div class="row">
              <div class="col-md-6">
                <h6 class="mb-3">Standard-Spalten:</h6>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="table-col-name"
                    v-model="visibleTableColumns"
                    value="name"
                  />
                  <label class="form-check-label" for="table-col-name">Name</label>
                </div>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="table-col-email"
                    v-model="visibleTableColumns"
                    value="email"
                  />
                  <label class="form-check-label" for="table-col-email">E-Mail</label>
                </div>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="table-col-role"
                    v-model="visibleTableColumns"
                    value="role"
                  />
                  <label class="form-check-label" for="table-col-role">Rolle</label>
                </div>
                <div class="form-check" v-if="selectedTeam?.refundMethod?.enabled">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="table-col-refundMethod"
                    v-model="visibleTableColumns"
                    value="refundMethod"
                  />
                  <label class="form-check-label" for="table-col-refundMethod"
                    >Erstattungsmethode</label
                  >
                </div>
              </div>
              <div class="col-md-6">
                <h6 class="mb-3">Bereich-Feld-Spalten:</h6>
                <div v-for="field in tableFields" :key="field.fieldKey" class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :id="`table-col-${field.fieldKey}`"
                    v-model="visibleTableColumns"
                    :value="field.fieldKey"
                  />
                  <label class="form-check-label" :for="`table-col-${field.fieldKey}`">{{
                    field.name || 'Unbekanntes Feld'
                  }}</label>
                </div>
              </div>
            </div>
            <div class="mt-4">
              <div class="d-flex gap-2">
                <button class="btn btn-outline-primary" @click="selectAllTableColumns">
                  <font-awesome-icon icon="check-square" class="me-2" />
                  Alle Spalten anzeigen
                </button>
                <button class="btn btn-outline-secondary" @click="resetTableColumns">
                  <font-awesome-icon icon="square" class="me-2" />
                  Standard-Spalten
                </button>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeTableModal">
              Schließen
            </button>
            <button type="button" class="btn btn-primary" @click="closeTableModal">
              Übernehmen
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { showSuccess, showError } from '@/services/notification'
import { teamService } from '@/api/teams'
import { useAuthStore } from '@/stores/auth'
import { useTeamsStore } from '@/stores/teams'
import { mapRoles } from '@/services/helper'
import Swal from 'sweetalert2'
import MemberFieldsInfo from './MemberFieldsInfo.vue'

const router = useRouter()
const route = useRoute()
const teamsStore = useTeamsStore()
const authStore = useAuthStore()

const { selectedTeam, loading, error } = storeToRefs(teamsStore)
const currentUser = computed(() => authStore.user)

const selectedColumns = ref(['name', 'email', 'role', 'refundMethod'])
const selectedMemberIds = ref([])
const exporting = ref(false)

const serverSideFiltering = ref(true)
const serverMembers = ref([])
const serverPagination = ref({
  total: 0,
  page: 1,
  limit: 25,
  totalPages: 0,
})
const serverLoading = ref(false)
let searchTimeout = null

const searchQuery = ref('')
const roleFilter = ref('')
const sortField = ref('name')
const sortDirection = ref('asc')

const currentPage = ref(1)
const pageSize = ref(25)

const joinRequests = ref([])
const loadingJoinRequests = ref(false)
const joinRequestsError = ref(null)
const processingRequest = ref(null)

const visibleTableColumns = ref([])

const allMembersSelected = computed(() => {
  if (paginatedMembers.value.length === 0) return false
  return paginatedMembers.value.every((member) =>
    selectedMemberIds.value.includes(member?.user?.id),
  )
})

const isAdminOrCreator = computed(() => {
  if (!selectedTeam.value || !currentUser.value) return false
  return (
    selectedTeam.value.role === 'teamAdmin' || selectedTeam.value.creatorId === currentUser.value.id
  )
})

const allMembers = computed(() => {
  if (serverSideFiltering.value) {
    return serverMembers.value
  }
  if (!selectedTeam.value?.member) return []
  return selectedTeam.value.member
})

const totalMembers = computed(() => {
  if (serverSideFiltering.value) {
    return serverPagination.value.total
  }
  return allMembers.value.length
})

const filteredMembers = computed(() => {
  if (serverSideFiltering.value) {
    return serverMembers.value
  }

  let members = [...allMembers.value]

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    members = members.filter(
      (member) =>
        `${member?.user?.firstName || ''} ${member?.user?.lastName || ''}`
          .toLowerCase()
          .includes(query) || member?.user?.email?.toLowerCase().includes(query),
    )
  }

  if (roleFilter.value) {
    if (roleFilter.value === 'creator') {
      members = members.filter((member) => member?.user?.id === selectedTeam.value?.creatorId)
    } else {
      members = members.filter((member) => member?.role === roleFilter.value)
    }
  }

  members.sort((a, b) => {
    let aValue, bValue

    switch (sortField.value) {
      case 'name':
        aValue = `${a?.user?.firstName || ''} ${a?.user?.lastName || ''}`.toLowerCase()
        bValue = `${b?.user?.firstName || ''} ${b?.user?.lastName || ''}`.toLowerCase()
        break
      case 'email':
        aValue = a?.user?.email?.toLowerCase() || ''
        bValue = b?.user?.email?.toLowerCase() || ''
        break
      case 'role':
        const roleOrder = { creator: 0, teamAdmin: 1, teamUser: 2 }
        aValue = a?.user?.id === selectedTeam.value?.creatorId ? 0 : roleOrder[a?.role] || 2
        bValue = b?.user?.id === selectedTeam.value?.creatorId ? 0 : roleOrder[b?.role] || 2
        break
      case 'refundMethod':
        const methodOrder = { paypal: 1, bank_transfer: 2, cash: 3 }
        const aHasRefund = a.refundData && a.refundData.refundMethod
        const bHasRefund = b.refundData && b.refundData.refundMethod

        if (aHasRefund && !bHasRefund) return -1
        if (!aHasRefund && bHasRefund) return 1
        if (!aHasRefund && !bHasRefund) return 0

        aValue = methodOrder[a.refundData.refundMethod] || 999
        bValue = methodOrder[b.refundData.refundMethod] || 999
        break
      default:
        if (sortField.value.startsWith('field_')) {
          const fieldId = sortField.value.replace('field_', '')
          const field = selectedTeam.value?.fields?.find(
            (f) => f.id === fieldId || f.id === parseInt(fieldId),
          )

          if (field?.responses) {
            const aFieldResponse = field.responses.find(
              (response) => response?.userId === a.user?.id,
            )
            const bFieldResponse = field.responses.find(
              (response) => response?.userId === b.user?.id,
            )

            aValue = aFieldResponse?.data || ''
            bValue = bFieldResponse?.data || ''

            if (field?.type === 'checkbox') {
              aValue = aValue === true || aValue === 'true' ? 1 : 0
              bValue = bValue === true || bValue === 'true' ? 1 : 0
            } else if (field?.type === 'number') {
              aValue = parseFloat(aValue) || 0
              bValue = parseFloat(bValue) || 0
            } else if (field?.type === 'date') {
              aValue = aValue ? new Date(aValue).getTime() : 0
              bValue = bValue ? new Date(bValue).getTime() : 0
            } else {
              aValue = String(aValue).toLowerCase()
              bValue = String(bValue).toLowerCase()
            }
          } else {
            aValue = ''
            bValue = ''
          }
        } else {
          return 0
        }
    }

    if (
      sortField.value === 'role' ||
      sortField.value === 'refundMethod' ||
      sortField.value.startsWith('field_')
    ) {
      return sortDirection.value === 'asc' ? aValue - bValue : bValue - aValue
    }

    if (aValue < bValue) return sortDirection.value === 'asc' ? -1 : 1
    if (aValue > bValue) return sortDirection.value === 'asc' ? 1 : -1
    return 0
  })

  return members
})

const totalPages = computed(() => {
  if (serverSideFiltering.value) {
    return serverPagination.value.totalPages
  }
  return Math.ceil(filteredMembers.value.length / pageSize.value)
})

const startIndex = computed(() => {
  if (serverSideFiltering.value) {
    return (serverPagination.value.page - 1) * serverPagination.value.limit
  }
  return (currentPage.value - 1) * pageSize.value
})

const paginatedMembers = computed(() => {
  if (serverSideFiltering.value) {
    return serverMembers.value
  }

  const start = startIndex.value
  const end = start + parseInt(pageSize.value)
  return filteredMembers.value.slice(start, end)
})

const visiblePages = computed(() => {
  const total = totalPages.value
  const current = currentPage.value
  const delta = 2

  let start = Math.max(1, current - delta)
  let end = Math.min(total, current + delta)

  if (end - start < 4) {
    if (start === 1) {
      end = Math.min(total, start + 4)
    } else if (end === total) {
      start = Math.max(1, end - 4)
    }
  }

  const pages = []
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }

  return pages
})

const teamFields = computed(() => (selectedTeam.value?.fields || []).filter(Boolean))

const exportFields = computed(() =>
  teamFields.value.map((field, index) => ({
    ...field,
    displayId: field?.id ?? index,
    fieldKey: `field_${field?.id ?? index}`,
  })),
)
const visibleTableFields = computed(() => {
  if (!isAdminOrCreator.value) return []
  return tableFields.value.filter((field) => visibleTableColumns.value.includes(field?.fieldKey))
})

const tableFields = computed(() =>
  teamFields.value.map((field, index) => ({
    ...field,
    displayId: field?.id ?? index,
    fieldKey: `field_${field?.id ?? index}`,
  })),
)

watch([searchQuery, roleFilter, pageSize], () => {
  currentPage.value = 1

  selectedMemberIds.value = []
})

watch(
  selectedTeam,
  (newTeam) => {
    if (newTeam?.fields && newTeam.fields.length > 0) {
      newTeam.fields.forEach((field, index) => {
        const fieldKey = `field_${field.id ?? index}`
        if (!visibleTableColumns.value.includes(fieldKey)) {
          visibleTableColumns.value.push(fieldKey)
        }
      })
    }

    if (newTeam?.member) {
      newTeam.member.forEach((member) => {})
    }
  },
  { immediate: true },
)

const selectAllMembers = () => {
  selectedMemberIds.value = paginatedMembers.value
    .filter((member) => member?.user?.id)
    .map((member) => member.user.id)
}

const deselectAllMembers = () => {
  selectedMemberIds.value = []
}

const toggleAllMembers = () => {
  if (allMembersSelected.value) {
    deselectAllMembers()
  } else {
    selectAllMembers()
  }
}

const selectAllTableColumns = () => {
  const allColumns = ['name', 'email', 'role']
  if (isAdminOrCreator.value) {
    allColumns.push('refundMethod')
  }
  if (selectedTeam.value?.fields) {
    selectedTeam.value.fields.forEach((field, index) => {
      allColumns.push(`field_${field.id ?? index}`)
    })
  }
  visibleTableColumns.value = allColumns
}

const resetTableColumns = () => {
  visibleTableColumns.value = ['name', 'email', 'role']
  if (isAdminOrCreator.value) {
    visibleTableColumns.value.push('refundMethod')
  }
}

const hasReceipts = (member) => {
  return member?.receiptCount > 0
}

const showExportModal = ref(false)
const showTableModal = ref(false)

const showExportSettingsModal = () => {
  showExportModal.value = true
}

const closeExportModal = () => {
  showExportModal.value = false
}

const showTableSettingsModal = () => {
  showTableModal.value = true
}

const closeTableModal = () => {
  showTableModal.value = false
}

const toggleMemberSelection = (member) => {
  if (!member?.user?.id) return

  const memberId = member.user.id
  const index = selectedMemberIds.value.indexOf(memberId)

  if (index > -1) {
    selectedMemberIds.value.splice(index, 1)
  } else {
    selectedMemberIds.value.push(memberId)
  }
}

const selectAllExportColumns = () => {
  const allColumns = ['name', 'email', 'role', 'refundMethod']
  if (selectedTeam.value?.fields) {
    selectedTeam.value.fields.forEach((field, index) => {
      allColumns.push(`field_${field.id ?? index}`)
    })
  }
  selectedColumns.value = allColumns
}

const resetExportColumns = () => {
  selectedColumns.value = ['name', 'email', 'role', 'refundMethod']
}

const exportSelectedMembers = async () => {
  if (selectedMemberIds.value.length === 0) {
    showError('Bitte wähle mindestens ein Mitglied aus')
    return
  }

  try {
    exporting.value = true

    const allAvailableColumns = ['name', 'email', 'role', 'refundMethod']
    if (selectedTeam.value?.fields) {
      selectedTeam.value.fields.forEach((field, index) => {
        allAvailableColumns.push(`field_${field.id ?? index}`)
      })
    }

    const response = await teamService.exportTeamMembersExcel(
      selectedTeam.value.id,
      selectedColumns.value,
      selectedMemberIds.value,
    )

    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `bereich_mitglieder_export_${selectedTeam.value.name}_${new Date().toISOString().split('T')[0]}.xlsx`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    showSuccess(`Excel-Export erfolgreich erstellt (${selectedMemberIds.value.length} Mitglieder)`)
  } catch (err) {
    console.error('Fehler beim Excel-Export:', err)
    showError('Excel-Export konnte nicht erstellt werden')
  } finally {
    exporting.value = false
  }
}

const exportMemberReceipts = async (member) => {
  if (!member?.user?.id) {
    showError('Ungültiges Mitglied')
    return
  }

  try {
    const loadingMessage = `Exportiere Quittungen für ${member.user.firstName || 'Unknown'} ${member.user.lastName || 'Unknown'}...`
    showSuccess(loadingMessage)

    const response = await teamService.exportMemberReceiptsZip(
      selectedTeam.value.id,
      member.user.id,
    )

    const blob = new Blob([response.data], { type: 'application/zip' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `quittungen_${member.user.firstName || 'Unknown'}_${member.user.lastName || 'Unknown'}_${selectedTeam.value.name}.zip`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    showSuccess(
      `ZIP-Export erfolgreich erstellt! Enthält ${member.receiptCount} Quittungen mit PDF-Dateien.`,
    )
  } catch (err) {
    console.error('Fehler beim ZIP-Export:', err)

    if (err.response?.status === 404) {
      showError(
        `Keine Quittungen für ${member.user.firstName || 'Unknown'} ${member.user.lastName || 'Unknown'} gefunden`,
      )
    } else if (err.response?.status === 403) {
      showError('Du hast keine Berechtigung, Quittungen zu exportieren')
    } else if (err.response?.status === 500) {
      showError('Server-Fehler beim Erstellen des ZIP-Exports. Bitte versuche es später erneut.')
    } else {
      showError('ZIP-Export konnte nicht erstellt werden. Bitte versuche es später erneut.')
    }
  }
}

const goToMemberReceipts = (member) => {
  const memberUserId = member?.user?.id
  if (!memberUserId) {
    showError('Ungültiges Mitglied')
    return
  }

  router.push({
    name: 'teams-receipts-admin',
    params: { id: route.params.id },
    query: { userId: memberUserId },
  })
}

const canRemoveMember = (member) => {
  if (!isAdminOrCreator.value) return false
  if (!member?.user?.id) return false
  if (member.user.id === currentUser.value?.id) return false

  if (selectedTeam.value?.creatorId === currentUser.value?.id) return true

  if (selectedTeam.value?.role === 'teamAdmin') {
    return member.role !== 'teamAdmin' && member.user.id !== selectedTeam.value?.creatorId
  }

  return false
}

const canPromoteToAdmin = (member) => {
  if (!isAdminOrCreator.value) return false
  if (!member?.user?.id) return false
  if (member.user.id === currentUser.value?.id) return false
  if (member.user.id === selectedTeam.value?.creatorId) return false
  if (member.role === 'teamAdmin') return false

  return true
}

const canDemoteFromAdmin = (member) => {
  if (!isAdminOrCreator.value) return false
  if (!member?.user?.id) return false
  if (member.user.id === currentUser.value?.id) return false
  if (member.user.id === selectedTeam.value?.creatorId) return false
  if (member.role !== 'teamAdmin') return false

  return true
}

const getMemberIcon = (member) => {
  if (!member?.user?.id) return 'user'
  if (member.user.id === selectedTeam.value?.creatorId) return 'crown'
  if (member.role === 'teamAdmin') return 'user-shield'
  return 'user'
}

const getMemberIconClass = (member) => {
  if (!member?.user?.id) return 'text-muted'
  if (member.user.id === selectedTeam.value?.creatorId) return 'text-warning'
  if (member.role === 'teamAdmin') return 'text-primary'
  return 'text-muted'
}

const getRoleBadgeClass = (member) => {
  if (!member?.user?.id) return 'bg-secondary'
  if (member.user.id === selectedTeam.value?.creatorId) return 'bg-warning'
  if (member.role === 'teamAdmin') return 'bg-primary'
  return 'bg-secondary'
}

const handleSearch = () => {
  if (serverSideFiltering.value) {
    if (searchTimeout) {
      clearTimeout(searchTimeout)
    }
    searchTimeout = setTimeout(() => {
      currentPage.value = 1
      loadServerMembers()
    }, 300)
  }
}

const clearSearch = () => {
  searchQuery.value = ''
  if (serverSideFiltering.value) {
    if (searchTimeout) {
      clearTimeout(searchTimeout)
    }
    currentPage.value = 1
    loadServerMembers()
  }
}

const handleFilterChange = () => {
  if (serverSideFiltering.value) {
    currentPage.value = 1
    loadServerMembers()
  }
}

const handlePageSizeChange = () => {
  if (serverSideFiltering.value) {
    currentPage.value = 1
    loadServerMembers()
  }
}

const clearAllFilters = () => {
  searchQuery.value = ''
  roleFilter.value = ''
  currentPage.value = 1
  selectedMemberIds.value = []

  if (serverSideFiltering.value) {
    loadServerMembers()
  }
}

const toggleSort = (field) => {
  if (sortField.value === field) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortField.value = field
    sortDirection.value = 'asc'
  }

  if (serverSideFiltering.value) {
    loadServerMembers()
  }
}

const goToPage = (page) => {
  if (serverSideFiltering.value) {
    if (page >= 1 && page <= serverPagination.value.totalPages) {
      currentPage.value = page
      loadServerMembers()
    }
  } else {
    if (page >= 1 && page <= totalPages.value) {
      currentPage.value = page
    }
  }
}

const nextPage = () => {
  if (serverSideFiltering.value) {
    if (currentPage.value < serverPagination.value.totalPages) {
      currentPage.value++
      loadServerMembers()
    }
  } else {
    if (currentPage.value < totalPages.value) {
      currentPage.value++
    }
  }
}

const previousPage = () => {
  if (serverSideFiltering.value) {
    if (currentPage.value > 1) {
      currentPage.value--
      loadServerMembers()
    }
  } else {
    if (currentPage.value > 1) {
      currentPage.value--
    }
  }
}

const removeMember = async (member) => {
  if (!selectedTeam.value || !member?.user?.id) return

  const result = await Swal.fire({
    title: 'Mitglied entfernen',
    text: `Möchtest du ${member?.user?.firstName || 'Unknown'} ${member?.user?.lastName || 'Unknown'} wirklich aus dem Bereich entfernen?`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#dc3545',
    cancelButtonColor: '#6c757d',
    confirmButtonText: 'Entfernen',
    cancelButtonText: 'Abbrechen',
  })

  if (!result.isConfirmed) return

  try {
    await teamService.removeMember(selectedTeam.value.id, member.user.id)
    showSuccess('Mitglied wurde entfernt')
    await teamsStore.loadTeamById(selectedTeam.value.id)
    await loadJoinRequests()
    await loadServerMembers()
    selectedMemberIds.value = selectedMemberIds.value.filter((id) => id !== member.user.id)

    const updatedTeam = await teamService.getTeam(selectedTeam.value.id)
    selectedTeam.value = updatedTeam
  } catch (err) {
    showError('Mitglied konnte nicht entfernt werden')
  }
}

const promoteMemberToAdmin = async (member) => {
  if (!selectedTeam.value || !member?.user?.id) return

  const result = await Swal.fire({
    title: 'Zum Administrator befördern',
    text: `Möchtest du ${member?.user?.firstName || 'Unknown'} ${member?.user?.lastName || 'Unknown'} wirklich zum Bereich-Administrator befördern?`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: '#0d6efd',
    cancelButtonColor: '#6c757d',
    confirmButtonText: 'Befördern',
    cancelButtonText: 'Abbrechen',
  })

  if (!result.isConfirmed) return

  try {
    await teamService.changeMemberRole(selectedTeam.value.id, member.user.id, 'teamAdmin')
    showSuccess(
      `${member?.user?.firstName || 'Unknown'} ${member?.user?.lastName || 'Unknown'} wurde zum Administrator befördert`,
    )
    await teamsStore.loadTeamById(selectedTeam.value.id)

    await loadJoinRequests()
    await loadServerMembers()
  } catch (err) {
    console.error('Fehler beim Befördern des Mitglieds:', err)
    showError('Mitglied konnte nicht befördert werden')
  }
}

const demoteMemberFromAdmin = async (member) => {
  if (!selectedTeam.value || !member?.user?.id) return

  const result = await Swal.fire({
    title: 'Admin-Rechte entziehen',
    text: `Möchtest du ${member?.user?.firstName || 'Unknown'} ${member?.user?.lastName || 'Unknown'} wirklich die Administrator-Rechte entziehen?`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#ffc107',
    cancelButtonColor: '#6c757d',
    confirmButtonText: 'Admin-Rechte entziehen',
    cancelButtonText: 'Abbrechen',
  })

  if (!result.isConfirmed) return

  try {
    await teamService.changeMemberRole(selectedTeam.value.id, member.user.id, 'teamUser')
    showSuccess(
      `${member?.user?.firstName || 'Unknown'} ${member?.user?.lastName || 'Unknown'} wurde zu einem normalen Mitglied degradiert`,
    )
    await teamsStore.loadTeamById(selectedTeam.value.id)

    const updatedTeam = await teamService.getTeam(selectedTeam.value.id)
    selectedTeam.value = updatedTeam
  } catch (err) {
    console.error('Fehler beim Entziehen der Admin-Rechte:', err)
    showError('Admin-Rechte konnten nicht entzogen werden')
  }
}

const loadJoinRequests = async () => {
  if (!selectedTeam.value?.id || !isAdminOrCreator.value) return

  try {
    loadingJoinRequests.value = true
    joinRequestsError.value = null

    const response = await teamService.getTeamJoinRequests(selectedTeam.value.id, 'pending')
    joinRequests.value = response.requests || []
  } catch (err) {
    console.error('Fehler beim Laden der Beitrittsanfragen:', err)

    if (err.response?.status === 403 || err.response?.status === 401) {
      showError('Du hast keine Berechtigung mehr für diesen Bereich')
      router.push('/teams')
      return
    }

    joinRequestsError.value = 'Beitrittsanfragen konnten nicht geladen werden'
  } finally {
    loadingJoinRequests.value = false
  }
}

const loadTeamRefundData = async () => {
  if (!selectedTeam.value?.id || !isAdminOrCreator.value) return

  try {
    const response = await teamService.getTeamRefundData(selectedTeam.value.id)
    const refundDataMap = {}

    response.forEach((refundData) => {
      refundDataMap[refundData.userId] = {
        refundMethod: refundData.refundMethod,
        paypalMeName: refundData.paypalMeName,
        iban: refundData.iban,
        bic: refundData.bic,
        accountHolder: refundData.accountHolder,
      }
    })

    if (selectedTeam.value.members) {
      selectedTeam.value.members.forEach((member) => {
        if (member?.user?.id) {
          member.refundData = refundDataMap[member.user.id] || null
        }
      })
    }
  } catch (err) {
    console.error('Fehler beim Laden der Erstattungsdaten:', err)
  }
}

const approveJoinRequest = async (requestId) => {
  try {
    processingRequest.value = requestId
    await teamService.approveJoinRequest(requestId)
    showSuccess('Beitrittsanfrage wurde genehmigt')

    joinRequests.value = joinRequests.value.filter((req) => req.id !== requestId)

    await teamsStore.loadTeamById(selectedTeam.value.id)
    await loadJoinRequests()
    await loadServerMembers()
  } catch (err) {
    console.error('Fehler beim Genehmigen der Anfrage:', err)
    showError('Beitrittsanfrage konnte nicht genehmigt werden')
  } finally {
    processingRequest.value = null
  }
}

const showRejectRequestModal = (request) => {
  Swal.fire({
    title: 'Beitrittsanfrage ablehnen',
    html: `
      <p>Möchtest du die Beitrittsanfrage von <strong>${request.user?.firstName} ${request.user?.lastName}</strong> wirklich ablehnen?</p>
      <div class="mt-3">
        <label for="rejectReason" class="form-label">Grund der Ablehnung (optional):</label>
        <textarea id="rejectReason" class="form-control" rows="3" placeholder="Grund angeben..."></textarea>
      </div>
    `,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#dc3545',
    cancelButtonColor: '#6c757d',
    confirmButtonText: 'Ablehnen',
    cancelButtonText: 'Abbrechen',
    preConfirm: () => {
      const reason = document.getElementById('rejectReason').value
      return reason
    },
  }).then((result) => {
    if (result.isConfirmed) {
      rejectJoinRequest(request.id, result.value)
    }
  })
}

const rejectJoinRequest = async (requestId, reason = '') => {
  try {
    await teamService.rejectJoinRequest(requestId, reason)
    showSuccess('Beitrittsanfrage wurde abgelehnt')
    await loadJoinRequests()
    await loadServerMembers()
    joinRequests.value = joinRequests.value.filter((req) => req.id !== requestId)
  } catch (err) {
    console.error('Fehler beim Ablehnen der Anfrage:', err)
    showError('Beitrittsanfrage konnte nicht abgelehnt werden')
  }
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleDateString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const getFieldTypeLabel = (type) => {
  if (!type) return 'Unbekannt'

  const labels = {
    text: 'Text',
    number: 'Zahl',
    email: 'E-Mail',
    tel: 'Telefon',
    date: 'Datum',
    checkbox: 'Checkbox',
    select: 'Auswahl',
    textarea: 'Textbereich',
  }
  return labels[type] || type
}

const formatFieldResponse = (field, response) => {
  if (!field || (response !== 0 && response !== false && !response)) {
    return '<em class="text-muted">Keine Antwort</em>'
  }

  switch (field.type) {
    case 'checkbox':
      if (typeof response === 'boolean') {
        return response
          ? '<span class="badge bg-success">Ja</span>'
          : '<span class="badge bg-secondary">Nein</span>'
      }

      if (typeof response === 'string') {
        const lowerResponse = response.toLowerCase().trim()
        if (['true', 'ja', 'yes', '1', 'on', 'checked'].includes(lowerResponse)) {
          return '<span class="badge bg-success">Ja</span>'
        }
        if (['false', 'nein', 'no', '0', 'off', 'unchecked'].includes(lowerResponse)) {
          return '<span class="badge bg-secondary">Nein</span>'
        }
      }

      if (typeof response === 'number') {
        return response !== 0
          ? '<span class="badge bg-success">Ja</span>'
          : '<span class="badge bg-secondary">Nein</span>'
      }

      return Boolean(response)
        ? '<span class="badge bg-success">Ja</span>'
        : '<span class="badge bg-secondary">Nein</span>'
    case 'select':
      return `<strong>${response}</strong>`
    case 'date':
      try {
        if (/^\d{4}-\d{2}-\d{2}$/.test(response)) {
          const date = new Date(response + 'T00:00:00')
          return `<strong>${date.toLocaleDateString('de-DE')}</strong>`
        } else {
          const date = new Date(response)
          if (!isNaN(date.getTime())) {
            return `<strong>${date.toLocaleDateString('de-DE')}</strong>`
          } else {
            return `<strong>${response}</strong>`
          }
        }
      } catch (e) {
        return `<strong>${response}</strong>`
      }
    case 'textarea':
      return `<div class="quitly-pre-wrap">${response}</div>`
    default:
      return `<strong>${response}</strong>`
  }
}

const getRefundMethodLabel = (method) => {
  if (!method) return 'Nicht konfiguriert'

  const labels = {
    paypal: 'PayPal',
    bank_transfer: 'Banküberweisung',
    cash: 'Bar',
  }
  return labels[method] || method
}

const getRefundMethodBadgeClass = (method) => {
  if (!method) return 'bg-secondary'

  const classes = {
    paypal: 'bg-primary',
    bank_transfer: 'bg-success',
    cash: 'bg-warning',
  }
  return classes[method] || 'bg-secondary'
}

const showUserResponses = (request) => {
  const fields = request.team?.fields || []
  const responses = request.userFieldResponses || {}

  let htmlContent = `
    <div class="user-responses-modal">
      <h5>Antworten von ${request.user?.firstName} ${request.user?.lastName}</h5>
      <hr>
  `

  if (fields.length === 0) {
    htmlContent += '<p class="text-muted">Keine Felder für dieses Team definiert.</p>'
  } else {
    fields.forEach((field) => {
      const response = responses[field.id]
      const hasResponse = response !== null && response !== undefined && response !== ''

      htmlContent += `
        <div class="mb-3">
          <div class="d-flex justify-content-between align-items-start">
            <strong>${field.name}</strong>
            ${field.required ? '<span class="badge bg-danger ms-2">Pflichtfeld</span>' : '<span class="badge bg-secondary ms-2">Optional</span>'}
          </div>
          <div class="mt-1">
            <small class="text-muted">${getFieldTypeLabel(field.type)}</small>
          </div>
          <div class="mt-2 p-2 bg-light border rounded">
            ${hasResponse ? formatFieldResponse(field, response) : '<em class="text-muted">Keine Antwort</em>'}
          </div>
        </div>
      `
    })
  }

  htmlContent += '</div>'

  Swal.fire({
    title: 'Nutzerantworten',
    html: htmlContent,
    width: '600px',
    showCloseButton: true,
    showConfirmButton: false,
    customClass: {
      popup: 'user-responses-popup',
    },
  })
}

const showMemberResponses = async (member) => {
  const fields = selectedTeam.value?.fields || []

  let htmlContent = `
    <div class="user-responses-modal">
      <h5>Antworten von ${member.user?.firstName} ${member.user?.lastName}</h5>
      <hr>
  `

  if (member.refundData && selectedTeam.value?.refundMethod?.enabled) {
    htmlContent += `
      <div class="mb-4 p-3 bg-info bg-opacity-10 border border-info rounded">
        <h6 class="mb-2">
          <font-awesome-icon icon="money-bill-wave" class="me-2" />
          Erstattungsdaten
        </h6>
        <div class="row">
          <div class="col-md-6">
            <strong>Methode:</strong>
            <span class="badge ${getRefundMethodBadgeClass(member.refundData.refundMethod)} ms-2">
              ${getRefundMethodLabel(member.refundData.refundMethod)}
            </span>
          </div>
        </div>
    `

    if (member.refundData.refundMethod === 'paypal') {
      htmlContent += `
        <div class="mt-2">
          <strong>PayPal.me Name:</strong> ${member.refundData.paypalMeName || 'Nicht angegeben'}
        </div>
      `
    } else if (member.refundData.refundMethod === 'bank_transfer') {
      let bankInfo = ''
      if (member.refundData.iban) {
        try {
          const { ibanApi } = await import('@/api/iban')
          const validationResult = await ibanApi.validateIban(member.refundData.iban)

          if (validationResult.valid && validationResult.bankName) {
            bankInfo = `
              <div class="mt-1">
                <strong>Bank:</strong> ${validationResult.bankName}
              </div>
            `
          }
        } catch (error) {
          console.error('IBAN-Validierungsfehler bei Mitgliederantworten:', error)
        }
      }

      htmlContent += `
        <div class="mt-2">
          <div><strong>Kontoinhaber:</strong> ${member.refundData.accountHolder || 'Nicht angegeben'}</div>
          <div><strong>IBAN:</strong> ${member.refundData.iban || 'Nicht angegeben'}</div>
          <div><strong>BIC:</strong> ${member.refundData.bic || 'Nicht angegeben'}</div>
          ${bankInfo}
        </div>
      `
    }

    htmlContent += '</div>'
  } else {
    if (selectedTeam.value?.refundMethod?.enabled && !member.refundData) {
      htmlContent += `
      <div class="mb-4 p-3 bg-warning bg-opacity-10 border border-warning rounded">
        <h6 class="mb-2">
          <font-awesome-icon icon="exclamation-triangle" class="me-2" />
          Erstattungsdaten
        </h6>
        <p class="mb-0 text-muted">Keine Erstattungsdaten konfiguriert</p>
      </div>
    `
    }
  }

  if (fields.length === 0) {
    htmlContent += '<p class="text-muted">Keine Felder für dieses Team definiert.</p>'
  } else {
    htmlContent += '<h6 class="mb-3">Team-Felder</h6>'
    fields.forEach((field) => {
      const fieldResponse = field.responses?.find((response) => response.userId === member.user?.id)
      const hasResponse =
        fieldResponse &&
        fieldResponse.data !== null &&
        fieldResponse.data !== undefined &&
        fieldResponse.data !== ''

      htmlContent += `
        <div class="mb-3">
          <div class="d-flex justify-content-between align-items-start">
            <strong>${field.name}</strong>
            ${field.required ? '<span class="badge bg-danger ms-2">Pflichtfeld</span>' : '<span class="badge bg-secondary ms-2">Optional</span>'}
          </div>
          <div class="mt-1">
            <small class="text-muted">${getFieldTypeLabel(field.type)}</small>
          </div>
          <div class="mt-2 p-2 bg-light border rounded">
            ${hasResponse ? formatFieldResponse(field, fieldResponse.data) : '<em class="text-muted">Keine Antwort</em>'}
          </div>
        </div>
      `
    })
  }

  htmlContent += '</div>'

  Swal.fire({
    title: 'Mitgliederantworten',
    html: htmlContent,
    width: '700px',
    showCloseButton: true,
    showConfirmButton: false,
    customClass: {
      popup: 'user-responses-popup',
    },
  })
}

const getFieldDisplayValue = (member, field) => {
  if (serverSideFiltering.value) {
    const fieldResponse = member.fieldResponses?.find(
      (response) => response.fieldId === field.id || response.fieldId === parseInt(field.id),
    )

    const hasResponse =
      fieldResponse &&
      fieldResponse.data !== null &&
      fieldResponse.data !== undefined &&
      fieldResponse.data !== ''

    return hasResponse
      ? formatFieldResponse(field, fieldResponse.data)
      : '<em class="text-muted">Keine Antwort</em>'
  } else {
    if (!field?.responses || !member?.user?.id) {
      return '<em class="text-muted">Keine Antwort</em>'
    }

    const fieldResponse = field.responses.find((response) => response?.userId === member.user.id)

    const hasResponse =
      fieldResponse &&
      fieldResponse.data !== null &&
      fieldResponse.data !== undefined &&
      fieldResponse.data !== ''

    return hasResponse
      ? formatFieldResponse(field, fieldResponse.data)
      : '<em class="text-muted">Keine Antwort</em>'
  }
}

const goBack = () => {
  router.push({ name: 'teams-settings', params: { id: route.params.id } })
}

const goToInvitationCodes = () => {
  router.push({ name: 'teams-invitation-codes', params: { id: route.params.id } })
}

const loadServerMembers = async () => {
  if (!selectedTeam.value?.id) return

  try {
    serverLoading.value = true

    const options = {
      page: currentPage.value,
      limit: pageSize.value,
      search: searchQuery.value,
      roleFilter: roleFilter.value,
      sortField: sortField.value,
      sortDirection: sortDirection.value,
    }

    const result = await teamService.getTeamMembers(selectedTeam.value.id, options)

    serverMembers.value = result.members || []
    serverPagination.value = result.pagination || {
      total: 0,
      page: 1,
      limit: 25,
      totalPages: 0,
    }

    selectedMemberIds.value = []
  } catch (err) {
    console.error('Fehler beim Laden der Team-Mitglieder:', err)
    showError('Bereich-Mitglieder konnten nicht geladen werden')
    serverMembers.value = []
    serverPagination.value = {
      total: 0,
      page: 1,
      limit: 25,
      totalPages: 0,
    }
  } finally {
    serverLoading.value = false
  }
}

onMounted(async () => {
  if (!selectedTeam.value || selectedTeam.value.id !== route.params.id) {
    await teamsStore.loadTeamById(route.params.id)
  }

  await teamsStore.loadTeamById(selectedTeam.value.id)
  await loadJoinRequests()
  await loadServerMembers()

  if (isAdminOrCreator.value) {
    await loadTeamRefundData()
  }

  visibleTableColumns.value = ['name', 'email', 'role']
  if (isAdminOrCreator.value) {
    visibleTableColumns.value.push('refundMethod')
  }
  selectAllExportColumns()
  if (!selectedTeam.value?.refundMethod?.enabled) {
    selectedColumns.value.splice(selectedColumns.value.indexOf('refundMethod'), 1)
  }

  await loadServerMembers()
})
</script>
